import React, { useEffect, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { useDispatch, useSelector } from 'react-redux';
import { selectPublications, selectAccessToken } from '../../store/selectors';
import { getPublications } from '../../store/actions';
import { IPublication } from '../../store/actions/sales-configuration';

// Include the Business Unit Code in the label so the MultiSelect's label filter can search it.
export function buildPublicationOptions(publications: IPublication[]): { label: string; value: string }[] {
  return publications
    .map((publication) => {
      const businessUnit = publication.businessUnit?.trim();
      return {
        label: `${publication.publicationCode} - ${publication.name}${businessUnit ? ` (${businessUnit})` : ''}`,
        value: publication.publicationCode,
      };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
}

interface IPublicationDropdownProperties {
  selectedValue: string[];
  onChange: (event: { value: string[] }) => void;
}

const PublicationDropdown: React.FC<IPublicationDropdownProperties> = ({ selectedValue, onChange }) => {
  const dispatch = useDispatch();
  const publications = useSelector(selectPublications);
  const accessToken = useSelector(selectAccessToken);
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (publications.length === 0 && accessToken) {
      dispatch(getPublications());
    }
  }, [dispatch, accessToken]);

  useEffect(() => {
    if (publications.length > 0) {
      setOptions(buildPublicationOptions(publications));
    }
  }, [publications]);

  const getLabelForCode = (code: string) => options.find((option) => option.value === code)?.label ?? code;

  return (
    <MultiSelect
      id="publicationDropdown"
      value={selectedValue}
      options={options}
      onChange={onChange}
      placeholder="Select Publications"
      display="chip"
      filter
      filterBy="label"
      optionLabel="value"
      itemTemplate={(option: { label: string; value: string }) => <span>{option.label}</span>}
      maxSelectedLabels={selectedValue.length > 0 ? selectedValue.length : 1}
      pt={{
        token: (tokenOptions) => {
          const context = tokenOptions?.context as unknown as { value: string } | undefined;
          return { title: context ? getLabelForCode(context.value) : undefined };
        },
      }}
    />
  );
};

export default PublicationDropdown;
